import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import ROUTES, { expandBlogRoutes, SITE_NAME } from "@/seo/routes";
import { BLOG_POSTS } from "@/content/blog";

// Only the real production origin should ever be crawlable.
// Preview, staging, dev and any local host inject noindex,nofollow so the
// preview build never competes with production for the same URLs.
const PROD_HOST = "thoratech.global";

/**
 * Runtime SEO head sync for the React SPA.
 * Mount once per page component (never inside a shared component like Nav).
 *
 * <Seo route="/careers" />                    // static routes
 * <Seo route="/blog/:slug" slug={slug} />     // dynamic routes
 */
export default function Seo({ route, slug = null }) {
  const meta = resolveMeta(route, slug);
  const [isProdHost, setIsProdHost] = useState(true);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const host = window.location.hostname.toLowerCase();
    setIsProdHost(host === PROD_HOST || host === `www.${PROD_HOST}`);
  }, []);

  if (!meta) return null;

  const { title, description, canonical, og, twitter, jsonLd } = meta;

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {/* Preview / staging must NEVER be indexed. */}
      {!isProdHost && (
        <meta name="robots" content="noindex,nofollow,noarchive" />
      )}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={og.type || "website"} />
      <meta property="og:title" content={og.title || title} />
      <meta property="og:description" content={og.description || description} />
      <meta property="og:url" content={canonical} />
      {og.image && <meta property="og:image" content={og.image} />}
      {og.image && <meta property="og:image:width" content="1200" />}
      {og.image && <meta property="og:image:height" content="630" />}
      {og.image && <meta property="og:image:alt" content={og.title || title} />}

      {/* Twitter */}
      <meta name="twitter:card" content={(twitter && twitter.card) || "summary_large_image"} />
      <meta name="twitter:title" content={og.title || title} />
      <meta name="twitter:description" content={og.description || description} />
      {og.image && <meta name="twitter:image" content={og.image} />}

      {/* JSON-LD blocks */}
      {(jsonLd || []).map((block, i) => (
        <script key={`ld-${i}`} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
}

function resolveMeta(route, slug) {
  if (route in ROUTES) return ROUTES[route];
  if (route === "/blog/:slug" && slug) {
    const post = BLOG_POSTS.find((p) => p.slug === slug);
    if (!post) return null;
    const [entry] = expandBlogRoutes([post]);
    return entry;
  }
  return null;
}
